import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Alert, AlertDescription } from './ui/alert';
import { Eye, EyeOff, UserPlus, AlertCircle, ArrowLeft } from 'lucide-react';
import { cadastrarCliente, buscarClientePorCpf } from '../services/api';
import { useToast } from '../hooks/use-toast';

const Register = () => {
  const [formData, setFormData] = useState({
    nome: '',
    email: '',
    cpfcnpj: '',
    celular: '',
    senha: '',
    confirmarSenha: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [cadastroRealizado, setCadastroRealizado] = useState(false);
  const [cpfJaCadastrado, setCpfJaCadastrado] = useState(false);
  const [verificandoCpf, setVerificandoCpf] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();

  // Verifica se CPF/CNPJ já está cadastrado
  const verificarCpfExistente = async (cpfcnpj) => {
    const cpfLimpo = cpfcnpj.replace(/\D/g, '');
    if (cpfLimpo.length < 11) {
      setCpfJaCadastrado(false);
      return;
    }
    
    setVerificandoCpf(true);
    try {
      await buscarClientePorCpf(cpfLimpo);
      // Se encontrou, CPF já está cadastrado
      setCpfJaCadastrado(true);
      setError('CPF/CNPJ já cadastrado. Por favor, faça login ou utilize a recuperação de senha.');
    } catch (err) {
      // Se não encontrou (404), CPF disponível
      setCpfJaCadastrado(false);
      if (error === 'CPF/CNPJ já cadastrado. Por favor, faça login ou utilize a recuperação de senha.') {
        setError('');
      }
    } finally {
      setVerificandoCpf(false);
    }
  };

  // Função para detectar se é CPF ou CNPJ e aplicar a máscara apropriada
  const formatCPFCNPJ = (value) => {
    const numbers = value.replace(/\D/g, '');
    
    if (numbers.length <= 11) {
      // CPF: XXX.XXX.XXX-XX
      return numbers
        .replace(/(\d{3})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d{1,2})$/, '$1-$2');
    } else {
      // CNPJ: XX.XXX.XXX/XXXX-XX
      return numbers
        .replace(/^(\d{2})(\d)/, '$1.$2')
        .replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3')
        .replace(/\.(\d{3})(\d)/, '.$1/$2')
        .replace(/(\d{4})(\d)/, '$1-$2');
    }
  };

  // Função para formatar telefone
  const formatTelefone = (value) => {
    const numbers = value.replace(/\D/g, '');
    
    if (numbers.length <= 10) {
      // Telefone fixo: (XX) XXXX-XXXX
      return numbers
        .replace(/^(\d{2})(\d)/, '($1) $2')
        .replace(/(\d{4})(\d)/, '$1-$2');
    } else {
      // Celular: (XX) XXXXX-XXXX
      return numbers
        .replace(/^(\d{2})(\d)/, '($1) $2')
        .replace(/(\d{5})(\d)/, '$1-$2');
    }
  };

  // Validação de CPF
  const validarCPF = (cpf) => {
    const numbers = cpf.replace(/\D/g, '');
    
    if (numbers.length !== 11) return false;
    if (/^(\d)\1{10}$/.test(numbers)) return false; // CPFs com todos os dígitos iguais
    
    let soma = 0;
    for (let i = 0; i < 9; i++) {
      soma += parseInt(numbers.charAt(i)) * (10 - i);
    }
    
    let resto = 11 - (soma % 11);
    if (resto === 10 || resto === 11) resto = 0;
    if (resto !== parseInt(numbers.charAt(9))) return false;
    
    soma = 0;
    for (let i = 0; i < 10; i++) {
      soma += parseInt(numbers.charAt(i)) * (11 - i);
    }
    
    resto = 11 - (soma % 11);
    if (resto === 10 || resto === 11) resto = 0;
    if (resto !== parseInt(numbers.charAt(10))) return false;
    
    return true;
  };

  // Validação de CNPJ
  const validarCNPJ = (cnpj) => {
    const numbers = cnpj.replace(/\D/g, '');
    
    if (numbers.length !== 14) return false;
    if (/^(\d)\1{13}$/.test(numbers)) return false; // CNPJs com todos os dígitos iguais
    
    let tamanho = numbers.length - 2;
    let numeros = numbers.substring(0, tamanho);
    let digitos = numbers.substring(tamanho);
    let soma = 0;
    let pos = tamanho - 7;
    
    for (let i = tamanho; i >= 1; i--) {
      soma += numeros.charAt(tamanho - i) * pos--;
      if (pos < 2) pos = 9;
    }
    
    let resultado = soma % 11 < 2 ? 0 : 11 - (soma % 11);
    if (resultado !== parseInt(digitos.charAt(0))) return false;
    
    tamanho = tamanho + 1;
    numeros = numbers.substring(0, tamanho);
    soma = 0;
    pos = tamanho - 7;
    
    for (let i = tamanho; i >= 1; i--) {
      soma += numeros.charAt(tamanho - i) * pos--;
      if (pos < 2) pos = 9;
    }
    
    resultado = soma % 11 < 2 ? 0 : 11 - (soma % 11);
    if (resultado !== parseInt(digitos.charAt(1))) return false;
    
    return true;
  };

  // Validação de email
  const validarEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleInputChange = (field, value) => {
    let formattedValue = value;

    if (field === 'cpfcnpj') {
      formattedValue = formatCPFCNPJ(value);
    } else if (field === 'celular') {
      formattedValue = formatTelefone(value);
    }

    setFormData(prev => ({
      ...prev,
      [field]: formattedValue
    }));
    setError('');
  };

  const validateForm = () => {
    // Validar nome
    if (!formData.nome.trim() || formData.nome.trim().length < 3) {
      setError('Por favor, insira um nome válido (mínimo 3 caracteres)');
      return false;
    }

    // Validar email
    if (!formData.email || !validarEmail(formData.email)) {
      setError('Por favor, insira um e-mail válido');
      return false;
    }

    // Validar CPF/CNPJ
    const numbers = formData.cpfcnpj.replace(/\D/g, '');
    if (numbers.length === 11) {
      if (!validarCPF(formData.cpfcnpj)) {
        setError('Por favor, insira um CPF válido');
        return false;
      }
    } else if (numbers.length === 14) {
      if (!validarCNPJ(formData.cpfcnpj)) {
        setError('Por favor, insira um CNPJ válido');
        return false;
      }
    } else {
      setError('Por favor, insira um CPF ou CNPJ válido');
      return false;
    }

    // Validar celular
    const celularNumbers = formData.celular.replace(/\D/g, '');
    if (!formData.celular || celularNumbers.length < 10) {
      setError('Por favor, insira um telefone válido');
      return false;
    }

    // Validar senha
    if (!formData.senha || formData.senha.length < 6) {
      setError('A senha deve ter pelo menos 6 caracteres');
      return false;
    }

    // Validar confirmação de senha
    if (formData.senha !== formData.confirmarSenha) {
      setError('As senhas não coincidem');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setLoading(true);
    setError('');
    
    try {
      const response = await cadastrarCliente({
        nome: formData.nome.trim(),
        email: formData.email.trim(),
        cpfcnpj: formData.cpfcnpj,
        celular: formData.celular,
        senha: formData.senha  // Envia senha em texto puro
      });
      
      if (response.success) {
        setCadastroRealizado(true);
        toast({
          title: "Cadastro realizado com sucesso!",
          description: "Seu cadastro será analisado e em breve você receberá a liberação para acessar o sistema.",
          duration: 8000,
        });
      }
    } catch (err) {
      console.error('Erro no cadastro:', err);
      setError(err.message || 'Erro ao realizar cadastro. Tente novamente.');
      
      toast({
        title: "Erro no cadastro",
        description: err.message || 'Erro ao realizar cadastro. Tente novamente.',
        variant: "destructive",
        duration: 5000,
      });
    } finally {
      setLoading(false);
    }
  };

  // Tela de sucesso após cadastro realizado
  if (cadastroRealizado) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <Card className="border-2 border-green-200">
            <CardHeader className="text-center bg-green-600 text-white rounded-t-lg">
              <CardTitle className="flex items-center justify-center space-x-2">
                <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span>Cadastro Realizado!</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="p-8 text-center">
              <div className="mb-6">
                <img
                  src="https://customer-assets.emergentagent.com/job_knorr-auction-proxy/artifacts/ud6gmi7h_Screenshot_7-removebg-preview.png"
                  alt="Knorr Leilões"
                  className="h-20 w-auto mx-auto mb-4"
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-4">
                Obrigado, {formData.nome.split(' ')[0]}!
              </h3>
              <p className="text-gray-600 mb-6">
                Seu cadastro foi recebido com sucesso. Nossa equipe irá analisar suas informações e em breve você receberá a liberação para acessar o sistema.
              </p>
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
                <p className="text-yellow-800 text-sm">
                  <strong>Atenção:</strong> Você receberá um comunicado quando seu acesso for liberado.
                </p>
              </div>
              <Button 
                onClick={() => navigate('/login')}
                className="w-full bg-[rgb(25,42,86)] hover:bg-[rgb(25,42,86)]/90"
              >
                Voltar para o Login
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <img
            src="https://customer-assets.emergentagent.com/job_knorr-auction-proxy/artifacts/ud6gmi7h_Screenshot_7-removebg-preview.png"
            alt="Knorr Leilões"
            className="h-16 w-auto mx-auto mb-4"
            onError={(e) => {
              e.target.style.display = 'none';
            }}
          />
          <p className="text-gray-600">Crie sua conta na Knorr Leilões</p>
        </div>

        <Card className="border-2 border-gray-200">
          <CardHeader className="text-center bg-[rgb(46,48,146)] text-white rounded-t-lg">
            <CardTitle className="flex items-center justify-center space-x-2">
              <UserPlus className="h-5 w-5" />
              <span>Criar Conta</span>
            </CardTitle>
          </CardHeader>

          <CardContent className="p-6">
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="nome" className="text-sm font-medium text-gray-700">
                  Nome Completo *
                </Label>
                <Input
                  id="nome"
                  type="text"
                  placeholder="Digite seu nome completo"
                  value={formData.nome}
                  onChange={(e) => handleInputChange('nome', e.target.value)}
                  className="mt-1 border-gray-300 focus:border-[rgb(46,48,146)] focus:ring-[rgb(46,48,146)]"
                  disabled={loading}
                />
              </div>

              <div>
                <Label htmlFor="email" className="text-sm font-medium text-gray-700">
                  E-mail *
                </Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="seu@email.com"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  className="mt-1 border-gray-300 focus:border-[rgb(46,48,146)] focus:ring-[rgb(46,48,146)]"
                  disabled={loading}
                />
              </div>

              <div>
                <Label htmlFor="cpfcnpj" className="text-sm font-medium text-gray-700">
                  CPF ou CNPJ *
                </Label>
                <Input
                  id="cpfcnpj"
                  type="text"
                  placeholder="000.000.000-00 ou 00.000.000/0000-00"
                  value={formData.cpfcnpj}
                  onChange={(e) => handleInputChange('cpfcnpj', e.target.value)}
                  onBlur={(e) => verificarCpfExistente(e.target.value)}
                  maxLength={18}
                  className={`mt-1 border-gray-300 focus:border-[rgb(46,48,146)] focus:ring-[rgb(46,48,146)] ${cpfJaCadastrado ? 'border-red-500 bg-red-50' : ''}`}
                  disabled={loading}
                />
                {verificandoCpf && (
                  <p className="text-sm text-gray-500 mt-1">Verificando...</p>
                )}
                {cpfJaCadastrado && (
                  <p className="text-sm text-red-600 mt-1">
                    CPF/CNPJ já cadastrado. <Link to="/login" className="underline font-medium">Faça login</Link> ou <Link to="/check-document" className="underline font-medium">recupere sua senha</Link>.
                  </p>
                )}
              </div>

              <div>
                <Label htmlFor="celular" className="text-sm font-medium text-gray-700">
                  Telefone/Celular *
                </Label>
                <Input
                  id="celular"
                  type="text"
                  placeholder="(11) 99999-9999"
                  value={formData.celular}
                  onChange={(e) => handleInputChange('celular', e.target.value)}
                  maxLength={15}
                  className="mt-1 border-gray-300 focus:border-[rgb(46,48,146)] focus:ring-[rgb(46,48,146)]"
                  disabled={loading}
                />
              </div>

              <div>
                <Label htmlFor="senha" className="text-sm font-medium text-gray-700">
                  Senha *
                </Label>
                <div className="relative mt-1">
                  <Input
                    id="senha"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Mínimo 6 caracteres"
                    value={formData.senha}
                    onChange={(e) => handleInputChange('senha', e.target.value)}
                    className="border-gray-300 focus:border-[rgb(46,48,146)] focus:ring-[rgb(46,48,146)] pr-10"
                    disabled={loading}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    disabled={loading}
                  >
                    {showPassword ? (
                      <EyeOff className="h-4 w-4 text-gray-400 hover:text-gray-600" />
                    ) : (
                      <Eye className="h-4 w-4 text-gray-400 hover:text-gray-600" />
                    )}
                  </button>
                </div>
              </div>

              <div>
                <Label htmlFor="confirmarSenha" className="text-sm font-medium text-gray-700">
                  Confirmar Senha *
                </Label>
                <div className="relative mt-1">
                  <Input
                    id="confirmarSenha"
                    type={showConfirmPassword ? 'text' : 'password'}
                    placeholder="Digite a senha novamente"
                    value={formData.confirmarSenha}
                    onChange={(e) => handleInputChange('confirmarSenha', e.target.value)}
                    className="border-gray-300 focus:border-[rgb(46,48,146)] focus:ring-[rgb(46,48,146)] pr-10"
                    disabled={loading}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    disabled={loading}
                  >
                    {showConfirmPassword ? (
                      <EyeOff className="h-4 w-4 text-gray-400 hover:text-gray-600" />
                    ) : (
                      <Eye className="h-4 w-4 text-gray-400 hover:text-gray-600" />
                    )}
                  </button>
                </div>
              </div>

              {error && (
                <Alert className="border-[rgb(238,29,37)] text-[rgb(238,29,37)]">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              <Button
                type="submit"
                className="w-full bg-[rgb(46,48,146)] hover:bg-[rgb(46,48,146)]/90 text-white py-3 transition-colors duration-200 disabled:bg-gray-400"
                disabled={loading || cpfJaCadastrado || verificandoCpf}
              >
                {loading ? (
                  <div className="flex items-center justify-center space-x-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    <span>Criando conta...</span>
                  </div>
                ) : (
                  <div className="flex items-center justify-center space-x-2">
                    <UserPlus className="h-4 w-4" />
                    <span>Criar Conta</span>
                  </div>
                )}
              </Button>
            </form>

            <div className="mt-6 text-center">
              <Link
                to="/login"
                className="text-[rgb(46,48,146)] hover:text-[rgb(46,48,146)]/80 text-sm font-medium flex items-center justify-center gap-2 hover:underline"
              >
                <ArrowLeft className="h-4 w-4" />
                Já tem uma conta? Faça login
              </Link>
            </div>
          </CardContent>
        </Card>

        <div className="text-center">
          <p className="text-xs text-gray-500">
            © 2025 Knorr Leilões. Sistema seguro e confiável.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Register;